import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import MModal from 'Components/modal/MModal/MModal';
import SFilterBox from 'Components/structural/SFilterBox/SFilterBox';
import { SFooter } from 'Components/structural/SFooter/SFooter';
import SNewContentCard from 'Components/structural/SNewContentCard/SNewContentCard';
import SPageLoaderWithTimer from 'Components/structural/SPageLoaderWithTimer/SPageLoaderWithTimer';
import UTextLink from 'Components/unit/UTextLink/UTextLink';
import { FilterState } from 'Libs/filter/types';
import { useAppDispatch } from 'Libs/redux/store';
import Enum from 'Models/Enum';
import User from 'Services/userService';
import { selectContentListItems, selectContentListMetadata } from 'Store/entities/content/list/content.list.selectors';
import { searchContents } from 'Store/entities/content/list/content.list.thunks';

import { ContentCreationModal } from '../ContentCreationModal/ContentCreationModal';
import { useContentActions } from '../ContentEdition/contentForm.hook';
import { ContentItem } from './ContentItem';
import styles, { NUMBER_OF_CARDS_PER_LINE } from './ContentList.style';
import { useContentFilters } from './hooks';
import { getContentFilterDescription } from './utils';

const NUMBER_OF_LINES_PER_PAGE = 4;
const CONTENT_PAGE_LIMIT = NUMBER_OF_LINES_PER_PAGE * NUMBER_OF_CARDS_PER_LINE;

export const ContentList: React.FC = () => {
  const dispatch = useAppDispatch();

  const determineNewContentModalVisibility = useMemo(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    if (urlParams.get('new') !== 'true') {
      return false;
    }

    urlParams.delete('new');
    const newQueryString = urlParams.toString();
    const newUrl = `${window.location.pathname}${newQueryString ? '?' + newQueryString : ''}`;
    window.history.replaceState(null, '', newUrl);

    return true;
  }, [window.location.search]);

  const [displayNewContentModal, setDisplayNewContentModal] = useState(determineNewContentModalVisibility);
  const [visibleArchiveWarningModal, displayArchiveWarningModal] = useState(false);
  const [visibleUnarchiveWarningModal, displayUnarchiveWarningModal] = useState(false);
  const [warningContentId, setWarningContentId] = useState<number>(0);

  const [offset, setOffset] = useState(0);
  const { loading, total } = useSelector(selectContentListMetadata);

  const { isFiltering, filters, storeFilters, setFilters, formatFiltersForStore } = useContentFilters();

  const shouldShowCount = useMemo(() => isFiltering || total > 0, [total, isFiltering]);
  const description = useMemo(() => getContentFilterDescription(total, isFiltering), [isFiltering, total]);
  const hasWriteRole = User.meSync().hasOneRole([Enum.Role.ROOT, Enum.Role.MASTER, Enum.Role.ADMIN, Enum.Role.WRITER]);

  const contents = useSelector(selectContentListItems);

  const { handleDuplicateContent, handleArchiveContent, handleUnarchiveContent } = useContentActions();

  useEffect(() => {
    const limit = offset === 0 ? CONTENT_PAGE_LIMIT - 1 : CONTENT_PAGE_LIMIT;

    dispatch(searchContents({ offset, limit, filters: storeFilters }));
  }, [offset, ...filters]);

  const handleFilterBoxChange = useCallback(
    (filterValues: FilterState[]) => {
      const newFilters = formatFiltersForStore(filterValues);

      setFilters(newFilters);
      setOffset(0);
    },
    [dispatch],
  );

  const canLoadMore = total > contents.length;

  const handlePress = useCallback(() => {
    if (loading) return;

    setOffset((prevOffset) =>
      prevOffset === 0 ? prevOffset + CONTENT_PAGE_LIMIT - 1 : prevOffset + CONTENT_PAGE_LIMIT,
    );
  }, [loading]);

  const shouldRenderLoader = loading && contents.length === 0;

  const defaultFiltersForContentCreation = useMemo(() => {
    const newFilters = formatFiltersForStore(filters);
    return {
      defaultLanguage: newFilters.languageCodes[0],
      defaultThematicId: newFilters.thematicId || undefined,
    };
  }, [filters]);

  const handleAction = useCallback((type: string, contentId: number) => {
    if (type === 'duplicate') {
      return handleDuplicateContent(contentId);
    }

    if (type === 'archive') {
      setWarningContentId(contentId);
      return displayArchiveWarningModal(true);
    }

    if (type === 'unarchive') {
      setWarningContentId(contentId);
      return displayUnarchiveWarningModal(true);
    }
  }, []);

  const handleArchiveAction = useCallback(() => {
    handleArchiveContent(warningContentId);
    displayArchiveWarningModal(false);
  }, [handleArchiveContent, warningContentId]);

  const handleUnarchiveAction = useCallback(() => {
    handleUnarchiveContent(warningContentId);
    displayUnarchiveWarningModal(false);
  }, [handleUnarchiveContent, warningContentId]);

  return (
    <div style={styles.wrapper}>
      <SFilterBox
        title={t('contents:contents_list.title')}
        description={description}
        placeholder={t('contents:contents_list.filters.placeholder')}
        filters={filters}
        count={shouldShowCount ? total : null}
        style={styles.filterBoxStyle}
        onChange={handleFilterBoxChange}
      />

      {shouldRenderLoader && (
        <div style={styles.pageLoader}>
          <SPageLoaderWithTimer isLoading={loading} style={styles.pageLoader} />
        </div>
      )}

      {!shouldRenderLoader && (
        <div style={styles.gridContainer}>
          <div style={styles.grid}>
            {hasWriteRole && (
              <SNewContentCard
                onClick={() => {
                  setDisplayNewContentModal(true);
                }}
                style={styles.newContentCard}
                type={total > 0 ? 'standard' : 'accentuated'}
                label={total > 0 ? '' : t('contents:contents_list.add_first_content')}
                data-test-id="new-content-card"
              />
            )}
            {contents.map((content) => (
              <ContentItem key={content.id} content={content} handleAction={handleAction} />
            ))}
          </div>
        </div>
      )}

      <ContentCreationModal
        visible={displayNewContentModal}
        defaultThematicId={defaultFiltersForContentCreation.defaultThematicId}
        defaultLanguage={defaultFiltersForContentCreation.defaultLanguage}
        onClose={() => {
          setDisplayNewContentModal(false);
        }}
      />

      <MModal
        type="dialog"
        visible={visibleArchiveWarningModal}
        actionButtonType="destructive"
        onCloseModal={() => displayArchiveWarningModal(false)}
        onSecondAction={() => displayArchiveWarningModal(false)}
        showExitCross
        title={t('contents:modals.archive.title')}
        description={t('contents:modals.archive.description')}
        labelActionButton={t('contents:modals.archive.confirm')}
        labelSecondButton={t('contents:modals.archive.cancel')}
        onAction={handleArchiveAction}
      />

      <MModal
        type="dialog"
        visible={visibleUnarchiveWarningModal}
        actionButtonType="destructive"
        onCloseModal={() => displayUnarchiveWarningModal(false)}
        onSecondAction={() => displayUnarchiveWarningModal(false)}
        showExitCross
        title={t('contents:modals.unarchive.title')}
        description={t('contents:modals.unarchive.description')}
        labelActionButton={t('contents:modals.unarchive.confirm')}
        labelSecondButton={t('contents:modals.unarchive.cancel')}
        onAction={handleUnarchiveAction}
      />

      <SFooter style={styles.loadMoreWrapper}>
        {canLoadMore && (
          <UTextLink
            text={t('contents:contents_list.load_more')}
            onClick={handlePress}
            type="secondary"
            rightIcon="bottom-chevron"
          />
        )}
      </SFooter>
    </div>
  );
};
